import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { Card, Box, IconButton } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';

import ClientCheckBox from '../clientFields/ClientCheckbox';
import ClientTextField from '../clientFields/ClientTextField';
import ClientDropDown from '../clientFields/ClientDropDown';
import { formatDate } from '../../utils/formatters';
import ClientTextarea from '../clientFields/ClientTextarea';

function DigitalCoOpTab({
  validateFunc,
  setFunction,
  data,
  hasTDCAdminPermission,
  structure,
  timeStamps,
  permissions = {},
  createdAt,
  isLoadingMMClients = false,
}) {
  const setter = setFunction;
  const [liveData, setLiveData] = useState({ ...data });
  const [userPermissions, setUserPermissions] = useState({ ...permissions });
  const [dataStructure, setDataStructure] = useState({ ...structure });
  const [formattedIsAdAIMember, setFormattedIsAdAIMember] = useState(null);
  const [emailConfigCount, setEmailConfigCount] = useState(0);
  const [emailConfig, setEmailConfig] = useState([]);
  const [smsConfig, setSmsConfig] = useState([]);
  const [smsConfigCount, setSmsConfigCount] = useState(0);
  const [reactivationConfigCount, setReactivationConfigCount] = useState(0);
  const [reactivationConfig, setReactivationConfig] = useState([]);

  const MAX_ROWS = 5;

  const GRID_CONTAINER_SX = {
    display: 'grid',
    // 5 equal columns (1fr) for data, 1 auto column for actions (buttons)
    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: '2fr 2fr 2fr 1.5fr 1fr auto' },
    gap: { xs: '12px', sm: '16px' },
    alignItems: 'center',
  };

  const CONFIG_MAP = {
    email: {
      getConfig: () => emailConfig,
      setConfig: setEmailConfig,
      setCount: setEmailConfigCount,
      count: emailConfigCount,
      field: 'email_configurations',
    },
    sms: {
      getConfig: () => smsConfig,
      setConfig: setSmsConfig,
      setCount: setSmsConfigCount,
      count: smsConfigCount,
      field: 'sms_configurations',
    },
    reactivation: {
      getConfig: () => reactivationConfig,
      setConfig: setReactivationConfig,
      setCount: setReactivationConfigCount,
      count: reactivationConfigCount,
      field: 'reactivation_configurations',
    },
  };

  const handleAddConfigRow = (type) => () => {
    const cfg = CONFIG_MAP[type];
    if (!cfg || cfg.count >= MAX_ROWS) return;

    const newId = uuidv4();
    const newIndex = cfg.getConfig().length;
    cfg.setCount((prev) => prev + 1);
    cfg.setConfig((prevConfigs) => {
      const flat = Array.isArray(prevConfigs) ? prevConfigs.flat() : [];
      return [...flat, {
        id: newId,
        type: '',
        proportion: '',
        details: { donor_type: '', days: '' },
        rank: newIndex + 1,
      }];
    });
    setter(cfg.field, newIndex + 1, 'rank', newId);
  };

  const handleRemoveConfigRow = (type, id) => () => {
    // remove row from email or sms config by id
    if (type === 'email') {
      setEmailConfigCount((prevCount) => prevCount - 1);
      setEmailConfig((prevConfigs) => {
        // ensure prevConfigs is a flat array
        const flattenedConfigs = Array.isArray(prevConfigs) ? prevConfigs.flat() : [];
        // remove the object with matching id
        const updatedConfigs = flattenedConfigs.filter((config) => config.id !== id);
        // return the updated array
        return updatedConfigs;
      });
      setter('email_configurations', null, 'remove', id);
    } else if (type === 'sms') {
      setSmsConfigCount((prevCount) => prevCount - 1);
      setSmsConfig((prevConfigs) => {
        // ensure prevConfigs is a flat array
        const flattenedConfigs = Array.isArray(prevConfigs) ? prevConfigs.flat() : [];
        // remove the object with matching id
        const updatedConfigs = flattenedConfigs.filter((config) => config.id !== id);
        // return the updated array
        return updatedConfigs;
      });
      setter('sms_configurations', null, 'remove', id);
    } else if (type === 'reactivation') {
      setReactivationConfigCount((prevCount) => prevCount - 1);
      setReactivationConfig((prevConfigs) => {
        // ensure prevConfigs is a flat array
        const flattenedConfigs = Array.isArray(prevConfigs) ? prevConfigs.flat() : [];
        // remove the object with matching id
        const updatedConfigs = flattenedConfigs.filter((config) => config.id !== id);
        // return the updated array
        return updatedConfigs;
      });
      setter('reactivation_configurations', null, 'remove', id);
    }
  };

  // Check if multi-member fields are still loading
  const isMultiMemberLoading = () => {
    if (isLoadingMMClients) return true;
    return dataStructure.multi_member_clients
      && dataStructure.multi_member_clients.length === 1
      && dataStructure.multi_member_clients[0] === 'loading...';
  };

  // filter the current client's own name out of the multi-member dropdown options
  const filterSelf = (list) => (Array.isArray(list)
    ? list.filter((name) => name !== liveData.client_full_name)
    : list);
  const mmStructure = {
    ...dataStructure,
    multi_member_clients: filterSelf(dataStructure.multi_member_clients),
    multi_member_mid_level_clients: filterSelf(dataStructure.multi_member_mid_level_clients),
    multi_member_sms_clients: filterSelf(dataStructure.multi_member_sms_clients),
  };

  // need to keep this function here to assign permanent ids to config objects that already exist
  const assignIds = (configs) => configs.map((config) => {
    // assign a uuid to each config for react key purposes
    // only assign if it doesn't already exist
    if (!config.id) {
      return {
        ...config,
        id: uuidv4(),
      };
    }
    return config;
  });

  useEffect(() => {
    setLiveData(data);
    // parse email and sms configurations if they exist and are strings
    if (data.email_configurations) {
      let emailConfigs;
      if (typeof data.email_configurations === 'string') {
        emailConfigs = JSON.parse(data.email_configurations) || [];
      } else {
        emailConfigs = data.email_configurations || [];
      }
      // assign ids for react keys
      const keyedEmailConfigs = assignIds(emailConfigs);
      // eslint-disable-next-line no-param-reassign
      data.email_configurations = keyedEmailConfigs;
      setEmailConfig(keyedEmailConfigs);
      setEmailConfigCount(keyedEmailConfigs.length || 0);
    }
    if (data.sms_configurations) {
      let smsConfigs;
      if (typeof data.sms_configurations === 'string') {
        smsConfigs = JSON.parse(data.sms_configurations) || [];
      } else {
        smsConfigs = data.sms_configurations || [];
      }
      // assign ids for react keys
      const keyedSmsConfigs = assignIds(smsConfigs);
      // eslint-disable-next-line no-param-reassign
      data.sms_configurations = keyedSmsConfigs;
      setSmsConfig(keyedSmsConfigs);
      setSmsConfigCount(keyedSmsConfigs.length || 0);
    }
    if (data.reactivation_configurations) {
      let reactivationConfigs;
      if (typeof data.reactivation_configurations === 'string') {
        reactivationConfigs = JSON.parse(data.reactivation_configurations) || [];
      } else {
        reactivationConfigs = data.reactivation_configurations || [];
      }
      // assign ids for react keys
      const keyedReactivationConfigs = assignIds(reactivationConfigs);
      // eslint-disable-next-line no-param-reassign
      data.reactivation_configurations = keyedReactivationConfigs;
      setReactivationConfig(keyedReactivationConfigs);
      setReactivationConfigCount(keyedReactivationConfigs.length || 0);
    }
    setUserPermissions(permissions);
    setDataStructure(structure);
    if (timeStamps && timeStamps.is_advantage_member) {
      setFormattedIsAdAIMember(formatDate(timeStamps.is_advantage_member));
    }
  }, [data, timeStamps, structure]);

  return (
    <>
      <h3 className="adai-section-header">TDC</h3>
      <div id="adai-columns">
        <div id="adai-info">
          <div className="timestamp">
            <ClientCheckBox
              setFunction={setter}
              data={liveData.is_advantage_member}
              attribute="is_advantage_member"
              label="Digital Co-Op Member"
              permissions={userPermissions}
              structure={dataStructure}
              isDisabled={!hasTDCAdminPermission}
              testingId="adaicheckbox-test"
            />
            {/* display last timestamp update or when the client was created */}
            <p>
              {`Last updated on ${timeStamps.is_advantage_member ? formattedIsAdAIMember : (formatDate(createdAt))}`}
            </p>
          </div>
          <ClientCheckBox
            setFunction={setter}
            data={liveData.is_mid_level_member}
            attribute="is_mid_level_member"
            label="Mid-Level Co-Op Member"
            permissions={userPermissions}
            structure={dataStructure}
            isDisabled={!hasTDCAdminPermission}
            testingId="mid-level-checkbox-test"
          />
          <ClientCheckBox
            setFunction={setter}
            data={liveData.expanded_window}
            attribute="expanded_window"
            label="Use Expanded Window"
            permissions={userPermissions}
            structure={dataStructure}
            isDisabled={!hasTDCAdminPermission}
            testingId="expanded-window-checkbox-test"
          />
          <ClientTextField
            setFunction={setter}
            data={liveData.advantageai_member_on_date}
            attribute="advantageai_member_on_date"
            label="Digital Co-Op Member Since"
            permissions={userPermissions}
            structure={dataStructure}
          />
        </div>
        <div id="adai-info">
          <ClientTextField
            setFunction={setter}
            data={liveData.suppressed_domains}
            attribute="suppressed_domains"
            label="Suppressed Domains"
            permissions={userPermissions}
            structure={dataStructure}
          />
          <ClientTextField
            validateFunc={validateFunc}
            setFunction={setter}
            data={liveData.share_cap_max_shares}
            attribute="share_cap_max_shares"
            label="Share Cap Max Shares"
            permissions={userPermissions}
            structure={dataStructure}
            inputType="number"
          />
          <ClientTextField
            validateFunc={validateFunc}
            setFunction={setter}
            data={liveData.share_cap_months}
            attribute="share_cap_months"
            label="Share Cap Months"
            permissions={userPermissions}
            structure={dataStructure}
            inputType="number"
          />
          <ClientTextarea
            validateFunc={validateFunc}
            setFunction={setter}
            data={liveData.notes}
            attribute="notes"
            label="Notes"
            permissions={userPermissions}
            structure={dataStructure}
          />
        </div>
      </div>
      <div id="adai-columns">
        <div id="adai-info">
          <h3 className="adai-section-header">Email</h3>
          <Card
            id="adai-delivery-selections"
            className="client-card"
            variant="outlined"
            sx={{ p: 3 }}
          >
            <div className="multi-member-card-container">
              <h2>Multi-Member Configuration</h2>
              {isMultiMemberLoading() ? (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  <p>Loading client options...</p>
                </div>
              ) : (
                <>
                  <div className="multi-member-card">
                    <p>Multi-Member Email Clients</p>
                    <ClientDropDown
                      setFunction={setter}
                      data={liveData.multi_member_clients
                        ? liveData.multi_member_clients[0]
                        : null}
                      attribute="multi_member_clients"
                      label="MM Email Client 1"
                      permissions={userPermissions}
                      structure={mmStructure}
                      isDisabled={!hasTDCAdminPermission}
                    />
                    <ClientDropDown
                      setFunction={setter}
                      data={liveData.multi_member_clients
                        ? liveData.multi_member_clients[1]
                        : null}
                      attribute="multi_member_clients"
                      label="MM Email Client 2"
                      permissions={userPermissions}
                      structure={mmStructure}
                      isDisabled={!hasTDCAdminPermission}
                    />
                    <ClientDropDown
                      setFunction={setter}
                      data={liveData.multi_member_clients
                        ? liveData.multi_member_clients[2]
                        : null}
                      attribute="multi_member_clients"
                      label="MM Email Client 3"
                      permissions={userPermissions}
                      structure={mmStructure}
                      isDisabled={!hasTDCAdminPermission}
                    />
                  </div>
                  <div className="multi-member-card-2">
                    <p>Multi-Member Mid-Level Clients</p>
                    <ClientDropDown
                      setFunction={setter}
                      data={liveData.multi_member_mid_level_clients
                        ? liveData.multi_member_mid_level_clients[0]
                        : null}
                      attribute="multi_member_mid_level_clients"
                      label="MM Mid-Level Client 1"
                      permissions={userPermissions}
                      structure={mmStructure}
                      isDisabled={!hasTDCAdminPermission}
                    />
                    <ClientDropDown
                      setFunction={setter}
                      data={liveData.multi_member_mid_level_clients
                        ? liveData.multi_member_mid_level_clients[1]
                        : null}
                      attribute="multi_member_mid_level_clients"
                      label="MM Mid-Level Client 2"
                      permissions={userPermissions}
                      structure={mmStructure}
                      isDisabled={!hasTDCAdminPermission}
                    />
                    <ClientDropDown
                      setFunction={setter}
                      data={liveData.multi_member_mid_level_clients
                        ? liveData.multi_member_mid_level_clients[2]
                        : null}
                      attribute="multi_member_mid_level_clients"
                      label="MM Mid-Level Client 3"
                      permissions={userPermissions}
                      structure={mmStructure}
                      isDisabled={!hasTDCAdminPermission}
                    />
                  </div>
                </>
              )}
            </div>
          </Card>
          <Card className="client-card" variant="outlined" sx={{ p: 3 }}>
            <h2>
              Email Donor Configuration
              <InfoIcon />
            </h2>
            <Box
              sx={{
                ...GRID_CONTAINER_SX,
                px: 2,
                paddingBottom: '5px',
                marginBottom: '10px',
                fontWeight: '800',
                '& p': { display: { xs: 'none', sm: 'block' } },
                '& p:nth-of-type(4), & p:nth-of-type(5)': { textAlign: 'center' },
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <p>Config Type</p>
              <p>Category</p>
              <p>Recency</p>
              <p>Proportion</p>
              <p>Rank</p>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {emailConfig && emailConfig.map((config) => (
                <Box
                  key={config.id}
                  sx={{
                    ...GRID_CONTAINER_SX,
                    p: 2,
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                    '& > div': {
                      gridColumn: { xs: 'span 1', sm: 'span 1' },
                    },
                  }}
                >
                  {/* Column 1: Config Type (Input) */}
                  <ClientDropDown
                    setFunction={(attribute, value, label) => setter('email_configurations', value, label, config.id)}
                    data={config.type}
                    attribute="donor_config_type"
                    label="type"
                    hideLabel
                    permissions={userPermissions}
                    structure={dataStructure}
                    isDisabled={!hasTDCAdminPermission}
                    customWidth="100%"
                    required
                  />
                  {/* Column 2: Category (Dropdown) */}
                  <ClientDropDown
                    setFunction={(attribute, value, label) => setter('email_configurations', value, label, config.id)}
                    data={config.details ? config.details.donor_type : ''}
                    attribute="donor_category"
                    label="donor_type"
                    hideLabel
                    permissions={userPermissions}
                    structure={dataStructure}
                    isDisabled={!hasTDCAdminPermission}
                    customWidth="100%"
                    required
                  />
                  {/* Column 3: Recency (Dropdown) */}
                  <ClientDropDown
                    setFunction={(attribute, value, label) => setter('email_configurations', value, label, config.id)}
                    data={config.details ? config.details.days : ''}
                    attribute="donor_recency"
                    label="days"
                    hideLabel
                    permissions={userPermissions}
                    structure={dataStructure}
                    isDisabled={!hasTDCAdminPermission}
                    customWidth="100%"
                  />
                  {/* Column 4: Proportion (Input - Number) */}
                  <ClientTextField
                    key={config.id}
                    keyProp={config.id}
                    validateFunc={validateFunc}
                    setFunction={(attribute, value, label) => setter('email_configurations', value, label, config.id)}
                    data={config.proportion}
                    attribute="proportion"
                    label="proportion"
                    hideLabel
                    permissions={userPermissions}
                    structure={dataStructure}
                    inputType="number"
                    customWidth="100%"
                    required
                  />
                  {/* Column 5: Rank (Input - Number) */}
                  <ClientTextField
                    validateFunc={validateFunc}
                    setFunction={(attribute, value, label) => setter('email_configurations', value, label, config.id)}
                    data={config.rank}
                    label="rank"
                    hideLabel
                    attribute="rank"
                    permissions={userPermissions}
                    structure={dataStructure}
                    inputType="number"
                    customWidth="100%"
                    required
                  />
                  {/* Column 6: Action Button */}
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <IconButton size="small" onClick={handleRemoveConfigRow('email', config.id)}>
                      <RemoveCircleIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton onClick={handleAddConfigRow('email')} disabled={!(emailConfigCount < MAX_ROWS)}>
                <AddCircleIcon data-testid="AddCircleIcon" />
              </IconButton>
              <p>Add New Config</p>
            </div>
          </Card>
          <Card className="client-card" variant="outlined" sx={{ p: 3 }}>
            <h2>
              Reactivation Donor Configuration
              <InfoIcon />
            </h2>
            <Box
              sx={{
                ...GRID_CONTAINER_SX,
                px: 2,
                paddingBottom: '5px',
                marginBottom: '10px',
                fontWeight: '800',
                '& p': { display: { xs: 'none', sm: 'block' } },
                '& p:nth-of-type(4), & p:nth-of-type(5)': { textAlign: 'center' },
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <p>Config Type</p>
              <p>Category</p>
              <p>Recency</p>
              <p>Proportion</p>
              <p>Rank</p>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {reactivationConfig && reactivationConfig.map((config) => (
                <Box
                  key={config.id}
                  sx={{
                    ...GRID_CONTAINER_SX,
                    p: 2,
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                    '& > div': {
                      gridColumn: { xs: 'span 1', sm: 'span 1' },
                    },
                  }}
                >
                  {/* Column 1: Config Type (Input) */}
                  <ClientDropDown
                    setFunction={(attribute, value, label) => setter('reactivation_configurations', value, label, config.id)}
                    data={config.type}
                    attribute="donor_config_type"
                    label="type"
                    hideLabel
                    permissions={userPermissions}
                    structure={dataStructure}
                    isDisabled={!hasTDCAdminPermission}
                    customWidth="100%"
                    required
                  />
                  {/* Column 2: Category (Dropdown) */}
                  <ClientDropDown
                    setFunction={(attribute, value, label) => setter('reactivation_configurations', value, label, config.id)}
                    data={config.details ? config.details.donor_type : ''}
                    attribute="donor_category"
                    label="donor_type"
                    hideLabel
                    permissions={userPermissions}
                    structure={dataStructure}
                    isDisabled={!hasTDCAdminPermission}
                    customWidth="100%"
                    required
                  />
                  {/* Column 3: Recency (Dropdown) */}
                  <ClientDropDown
                    setFunction={(attribute, value, label) => setter('reactivation_configurations', value, label, config.id)}
                    data={config.details ? config.details.days : ''}
                    attribute="donor_recency"
                    label="days"
                    hideLabel
                    permissions={userPermissions}
                    structure={dataStructure}
                    isDisabled={!hasTDCAdminPermission}
                    customWidth="100%"
                  />
                  {/* Column 4: Proportion (Input - Number) */}
                  <ClientTextField
                    key={config.id}
                    keyProp={config.id}
                    validateFunc={validateFunc}
                    setFunction={(attribute, value, label) => setter('reactivation_configurations', value, label, config.id)}
                    data={config.proportion}
                    attribute="proportion"
                    label="proportion"
                    hideLabel
                    permissions={userPermissions}
                    structure={dataStructure}
                    inputType="number"
                    customWidth="100%"
                    required
                  />
                  {/* Column 5: Rank (Input - Number) */}
                  <ClientTextField
                    validateFunc={validateFunc}
                    setFunction={(attribute, value, label) => setter('reactivation_configurations', value, label, config.id)}
                    data={config.rank}
                    label="rank"
                    hideLabel
                    attribute="rank"
                    permissions={userPermissions}
                    structure={dataStructure}
                    inputType="number"
                    customWidth="100%"
                    required
                  />
                  {/* Column 6: Action Button */}
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <IconButton size="small" onClick={handleRemoveConfigRow('reactivation', config.id)}>
                      <RemoveCircleIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton onClick={handleAddConfigRow('reactivation')} disabled={!(reactivationConfigCount < MAX_ROWS)}>
                <AddCircleIcon data-testid="AddCircleIcon" />
              </IconButton>
              <p>Add New Config</p>
            </div>
          </Card>
          <Card className="adai-email-validation-provider" variant="outlined" sx={{ p: 3 }}>
            <h2>Email Validation Provider</h2>
            <ClientDropDown
              setFunction={setter}
              data={liveData.email_validation_provider}
              attribute="email_validation_provider"
              label="Email Validation Provider"
              permissions={userPermissions}
              structure={dataStructure}
              isDisabled={!hasTDCAdminPermission}
            />
          </Card>
        </div>
        <div id="adai-info">
          <h3 className="adai-section-header">SMS</h3>
          <Card className="client-card" variant="outlined" sx={{ p: 3 }}>
            <div className="multi-member-card-container">
              <h2>Multi-Member Configuration</h2>
              {isMultiMemberLoading() ? (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  <p>Loading client options...</p>
                </div>
              ) : (
                <div className="multi-member-card">
                  <p>Multi-Member SMS Clients</p>
                  <ClientDropDown
                    setFunction={setter}
                    data={liveData.multi_member_sms_clients
                      ? liveData.multi_member_sms_clients[0]
                      : null}
                    attribute="multi_member_sms_clients"
                    label="MM SMS Client 1"
                    permissions={userPermissions}
                    structure={mmStructure}
                    isDisabled={!hasTDCAdminPermission}
                  />
                  <ClientDropDown
                    setFunction={setter}
                    data={liveData.multi_member_sms_clients
                      ? liveData.multi_member_sms_clients[1]
                      : null}
                    attribute="multi_member_sms_clients"
                    label="MM SMS Client 2"
                    permissions={userPermissions}
                    structure={mmStructure}
                    isDisabled={!hasTDCAdminPermission}
                  />
                  <ClientDropDown
                    setFunction={setter}
                    data={liveData.multi_member_sms_clients
                      ? liveData.multi_member_sms_clients[2]
                      : null}
                    attribute="multi_member_sms_clients"
                    label="MM SMS Client 3"
                    permissions={userPermissions}
                    structure={mmStructure}
                    isDisabled={!hasTDCAdminPermission}
                  />
                </div>
              )}
            </div>
          </Card>
          <Card className="client-card" variant="outlined" sx={{ p: 3 }}>
            <h2>
              SMS Donor Configuration
              <InfoIcon />
            </h2>
            <Box
              sx={{
                ...GRID_CONTAINER_SX,
                px: 2,
                paddingBottom: '12px',
                marginBottom: '16px',
                fontWeight: '800',
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                '& p': { display: { xs: 'none', sm: 'block' } },
                '& p:nth-of-type(4), & p:nth-of-type(5)': { textAlign: 'center' },
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <p>Config Type</p>
              <p>Category</p>
              <p>Recency</p>
              <p>Proportion</p>
              <p>Rank</p>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {smsConfig && smsConfig.map((config) => (
                <Box
                  key={config.id}
                  sx={{
                    ...GRID_CONTAINER_SX,
                    p: 2,
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                    '& > div': {
                      gridColumn: { xs: 'span 1', sm: 'span 1' },
                    },
                  }}
                >
                  {/* Column 1: Config Type (Input) */}
                  <ClientDropDown
                    setFunction={(attribute, value, label) => setter('sms_configurations', value, label, config.id)}
                    data={config.type}
                    attribute="donor_config_type"
                    label="type"
                    hideLabel
                    permissions={userPermissions}
                    structure={dataStructure}
                    isDisabled={!hasTDCAdminPermission}
                    customWidth="100%"
                    required
                  />
                  {/* Column 2: Category (Dropdown) */}
                  <ClientDropDown
                    setFunction={(attribute, value, label) => setter('sms_configurations', value, label, config.id)}
                    data={config.details ? config.details.donor_type : ''}
                    attribute="donor_category"
                    label="donor_type"
                    hideLabel
                    permissions={userPermissions}
                    structure={dataStructure}
                    isDisabled={!hasTDCAdminPermission}
                    customWidth="100%"
                    required
                  />
                  {/* Column 3: Recency (Dropdown) */}
                  <ClientDropDown
                    setFunction={(attribute, value, label) => setter('sms_configurations', value, label, config.id)}
                    data={config.details ? config.details.days : ''}
                    attribute="donor_recency"
                    label="days"
                    hideLabel
                    permissions={userPermissions}
                    structure={dataStructure}
                    isDisabled={!hasTDCAdminPermission}
                    customWidth="100%"
                  />
                  {/* Column 4: Proportion (Input - Number) */}
                  <ClientTextField
                    key={config.id}
                    keyProp={config.id}
                    validateFunc={validateFunc}
                    setFunction={(attribute, value, label) => setter('sms_configurations', value, label, config.id)}
                    data={config.proportion}
                    attribute="proportion"
                    label="proportion"
                    hideLabel
                    permissions={userPermissions}
                    structure={dataStructure}
                    inputType="number"
                    customWidth="100%"
                    required
                  />
                  {/* Column 5: Rank (Input - Number) */}
                  <ClientTextField
                    validateFunc={validateFunc}
                    setFunction={(attribute, value, label) => setter('sms_configurations', value, label, config.id)}
                    data={config.rank}
                    label="rank"
                    hideLabel
                    attribute="rank"
                    permissions={userPermissions}
                    structure={dataStructure}
                    inputType="number"
                    customWidth="100%"
                    required
                  />
                  {/* Column 6: Action Button */}
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <IconButton size="small" onClick={handleRemoveConfigRow('sms', config.id)}>
                      <RemoveCircleIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton onClick={handleAddConfigRow('sms')} disabled={!(smsConfigCount < MAX_ROWS)}>
                <AddCircleIcon />
              </IconButton>
              <p>Add New Config</p>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

export default DigitalCoOpTab;

DigitalCoOpTab.propTypes = {
  permissions: PropTypes.oneOfType([PropTypes.object]),
  structure: PropTypes.oneOfType([PropTypes.object]),
  setFunction: PropTypes.func,
  validateFunc: PropTypes.func,
  data: PropTypes.oneOfType([PropTypes.object]).isRequired,
  timeStamps: PropTypes.oneOfType([PropTypes.object]),
  hasTDCAdminPermission: PropTypes.bool.isRequired,
  createdAt: PropTypes.string,
  isLoadingMMClients: PropTypes.bool,
};
